import { Form } from 'antd';
import { FormProviderProps } from 'antd/es/form/context';
import dayjs from 'dayjs';
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { ACTIVITY_ANALYZE_SCOPE } from 'business/shift-report/report-analyze/pages/activity-analyze/constants';
import {
  ACTIVITY_AGGREGATION_LEVELS_MAP,
  ACTIVITY_CRITICAL_TYPES_MAP,
  ACTIVITY_DISPLAY_TYPES_MAP,
  ACTIVITY_GROUP_BY_TYPES_MAP,
} from 'business/shift-report/report-analyze/pages/activity-analyze/types';
import {
  getLocalStorageValue,
  insertOrUpdateLocalStorageValue,
} from 'technical/local-storage';
import { DateSelectionTypeEnum } from 'ui/form/temporality-select-input';

import { defaultFiltersNoDate, getDefaultFilters } from './constants';
import {
  parseActivityAnalyzeCheckedActivities,
  parseActivityAnalyzeFilters,
} from './local-storage';
import {
  ActivityAnalyzeLocalStorageSchema,
  ActivityAnalyzeRangeFilters,
  ReportActivityAnalyzeFilters,
  activityAnalyzeLocalStorageSchema,
} from './types';

const FILTERS_FORM_NAME = 'report-activity-analyze-filters-form';
const CHECKED_ACTIVIES_FORM_NAME = 'report-activity-analyze-activities-form';

const ReportActivityAnalyzeFiltersContext =
  createContext<ReportActivityAnalyzeFilters>({
    filters: defaultFiltersNoDate,
    checkedActivities: [],
  });

const ReportActivityAnalyzeFiltersProvider = ({
  constructionSiteId,
  allActivitiesChecked,
  noDefaultFor,
  children,
}: PropsWithChildren<{
  constructionSiteId: string;
  allActivitiesChecked: string[];
  noDefaultFor?: DateSelectionTypeEnum[];
}>) => {
  const storedOptions = getLocalStorageValue<ActivityAnalyzeLocalStorageSchema>(
    {
      scope: ACTIVITY_ANALYZE_SCOPE,
      key: constructionSiteId,
      schema: activityAnalyzeLocalStorageSchema,
    },
  );

  const [filters, setFilters] = useState<ActivityAnalyzeRangeFilters>(
    parseActivityAnalyzeFilters(storedOptions?.filters) ?? getDefaultFilters(),
  );

  const [checkedActivities, setCheckedActivities] = useState(
    parseActivityAnalyzeCheckedActivities(storedOptions?.checkedActivities) ??
      [],
  );

  useEffect(() => {
    if (checkedActivities.length === 0 && allActivitiesChecked.length > 0) {
      setCheckedActivities(allActivitiesChecked);
      insertOrUpdateLocalStorageValue({
        scope: ACTIVITY_ANALYZE_SCOPE,
        key: constructionSiteId,
        value: { filters, checkActivities: allActivitiesChecked },
      });
    }

    if (!allActivitiesChecked.some((id) => checkedActivities.includes(id))) {
      setCheckedActivities(
        storedOptions?.checkedActivities ?? allActivitiesChecked,
      );
    }

    // No need to check for filters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkedActivities.length,
    allActivitiesChecked.length,
    constructionSiteId,
  ]);

  const onFormChange: FormProviderProps['onFormChange'] = (
    formName,
    { forms },
  ) => {
    if (formName === FILTERS_FORM_NAME) {
      const form = forms[formName];

      const dateRange =
        form.getFieldValue('filterType') === 'date' &&
        // Check for `period` included in noDefaultFor to empty the input when
        // it is changed back to date filter type from ring filter type
        noDefaultFor?.includes(DateSelectionTypeEnum.Period)
          ? [null, null]
          : [dayjs().subtract(1, 'week'), dayjs()];

      const values = {
        dateRange,
        ringRange: [null, null],
        groupByType: ACTIVITY_GROUP_BY_TYPES_MAP.day,
        displayType: ACTIVITY_DISPLAY_TYPES_MAP.bar,
        aggregationLevel: ACTIVITY_AGGREGATION_LEVELS_MAP[1],
        criticalType: ACTIVITY_CRITICAL_TYPES_MAP.all,
        vChartScale: null,
        ...form.getFieldsValue(),
      } satisfies ActivityAnalyzeRangeFilters;

      // Setting default range for both day and ring because Antd Form remove the fields value if the input is
      // not rendered. Since we conditionnaly render the dateRange or ringRange depending on the `filterType`
      // it is required to add them with defaut value to the FiltersProvider. Therefore, we're sure it is always
      // defined when the filterType is changed
      setFilters(values);
      form.setFieldsValue(values);
      insertOrUpdateLocalStorageValue({
        scope: ACTIVITY_ANALYZE_SCOPE,
        key: constructionSiteId,
        value: { filters: values, checkedActivities },
      });
    }

    if (formName === CHECKED_ACTIVIES_FORM_NAME) {
      const form = forms[formName];
      const values = form.getFieldValue('checkedActivities');

      setCheckedActivities(values);
      insertOrUpdateLocalStorageValue({
        scope: ACTIVITY_ANALYZE_SCOPE,
        key: constructionSiteId,
        value: { filters, checkedActivities: values },
      });
    }
  };

  return (
    <ReportActivityAnalyzeFiltersContext.Provider
      value={{ filters, checkedActivities: checkedActivities }}
    >
      <Form.Provider onFormChange={onFormChange}>{children}</Form.Provider>
    </ReportActivityAnalyzeFiltersContext.Provider>
  );
};

const useReportActivityAnalyzeFiltersFormName = () => FILTERS_FORM_NAME;
const useReportActivityAnalyzeCheckedActivitiesFormName = () =>
  CHECKED_ACTIVIES_FORM_NAME;

const useReportActivityAnalyzeFilters = () =>
  useContext(ReportActivityAnalyzeFiltersContext).filters;

const useReportActivityAnalyzeActivities = () =>
  useContext(ReportActivityAnalyzeFiltersContext).checkedActivities;

export {
  ReportActivityAnalyzeFiltersProvider,
  useReportActivityAnalyzeFiltersFormName,
  useReportActivityAnalyzeCheckedActivitiesFormName,
  useReportActivityAnalyzeFilters,
  useReportActivityAnalyzeActivities,
};
